/**
 * Batch metadata for the patient field panel's section groups: the counts the
 * headers show, and the rule that decides which batch opens.
 *
 * The panel (BridgeFieldsForm) has always grouped its rows by the form's own
 * section labels. What it lacked was the onboarding wizard's SHAPE — numbered
 * batches with a count, and the page opening on whatever still needs the
 * patient. Michael asked for the information after the card step to be batched
 * the way initial onboarding is (2026-10-19).
 *
 * `needsAttention` and `isFilled` are injected rather than recomputed here, so
 * this stays pure — testable without mounting the panel — and cannot drift from
 * the decision each row makes for itself (a malformed value, or
 * required-and-empty).
 */

export interface BatchStat {
  title: string;
  index: number;
  total: number;
  /** Rows that still need the patient: malformed, or required and empty. */
  attention: number;
  /** Rows that carry a value and need nothing. */
  filled: number;
  complete: boolean;
}

export interface BatchStatuses {
  stats: BatchStat[];
  /** Titles to open: every incomplete batch, or the first when all are done. */
  open: string[];
}

export function batchStatuses<T>(
  groups: { title: string; fields: T[] }[],
  needsAttention: (f: T) => boolean,
  isFilled: (f: T) => boolean,
): BatchStatuses {
  const stats: BatchStat[] = groups.map((g, i) => {
    const attention = g.fields.filter(needsAttention).length;
    return {
      title: g.title,
      index: i,
      total: g.fields.length,
      attention,
      filled: g.fields.filter((f) => isFilled(f) && !needsAttention(f)).length,
      complete: attention === 0,
    };
  });

  // Open every batch that needs something, and nothing else. When NOTHING
  // needs attention — the common case on this page, where the fields are
  // optional and the real work happened during onboarding — open the first, so
  // the page never presents itself as a wall of closed drawers.
  const needing = stats.filter((s) => !s.complete).map((s) => s.title);
  const open = needing.length ? needing : stats.slice(0, 1).map((s) => s.title);
  return { stats, open };
}
